import { useEffect, useRef, useState } from 'react'
import { useGameStore } from '../../store/gameStore'
import type { DanmakuCategory } from '../../types'
import './DanmakuLayer.css'

/**
 * DanmakuLayer —— 答题弹幕浮层
 *
 * 监听 store.pendingDanmaku, 每次新弹幕进来就插入一条从右向左飘的横条。
 * 多条并发时按插入顺序分配垂直轨道 (上下分层避免重叠)。
 * 8 秒后自动消失, 不挡答题 (pointer-events: none)。
 */

interface ActiveDanmaku {
  id: string
  text: string
  category: DanmakuCategory
  lane: number
  spawnedAt: number
}

const DANMAKU_DURATION_MS = 8000
const LANE_HEIGHT = 48  // px, 单条弹幕行高
const MAX_LANES = 4     // 最多同时 4 条, 超出排队等下一轨道
const FADE_OUT_AT = 1500 // 离场前 1.5s 开始淡出

function categoryColor(category: DanmakuCategory): { border: string; bg: string; text: string } {
  switch (category) {
    case 'praise':
      return {
        border: 'var(--color-gold)',
        bg: 'rgba(251, 211, 141, 0.92)',
        text: '#5c3a00',
      }
    case 'hot':
      return {
        border: 'var(--color-bull)',
        bg: 'rgba(255, 158, 198, 0.95)',
        text: '#5a0033',
      }
    case 'roast':
      return {
        border: 'var(--color-bear)',
        bg: 'rgba(143, 217, 182, 0.92)',
        text: '#0f3a25',
      }
    case 'warn':
      return {
        border: 'var(--color-warning)',
        bg: 'rgba(255, 199, 138, 0.95)',
        text: '#4a1f00',
      }
    case 'cheer':
      return {
        border: 'var(--color-cyan)',
        bg: 'rgba(165, 180, 252, 0.92)',
        text: '#1e1b4b',
      }
  }
}

export function DanmakuLayer() {
  const pendingDanmaku = useGameStore(s => s.pendingDanmaku)
  const danmakuTick = useGameStore(s => s.danmakuTick)
  const consumePendingDanmaku = useGameStore(s => s.consumePendingDanmaku)
  const loadDanmaku = useGameStore(s => s.loadDanmaku)

  const [items, setItems] = useState<ActiveDanmaku[]>([])
  const laneRef = useRef<number>(0)
  const lastTickRef = useRef<number>(0)

  // 首次挂载时异步加载弹幕语料 (兜底, store.initialize 没拉到时补救)
  useEffect(() => {
    loadDanmaku()
  }, [loadDanmaku])

  // 监听 pendingDanmaku 变化, 通过 danmakuTick 强制刷新
  useEffect(() => {
    if (danmakuTick === lastTickRef.current) return
    lastTickRef.current = danmakuTick
    if (!pendingDanmaku) return

    const lane = laneRef.current % MAX_LANES
    laneRef.current = (laneRef.current + 1) % MAX_LANES

    const item: ActiveDanmaku = {
      id: pendingDanmaku.id + '-' + Date.now(),
      text: pendingDanmaku.text,
      category: pendingDanmaku.category,
      lane,
      spawnedAt: Date.now(),
    }
    setItems(prev => [...prev, item])

    // 8s 后自动移除 (前 6.5s 飘过, 后 1.5s 淡出)
    const timer = setTimeout(() => {
      setItems(prev => prev.filter(x => x.id !== item.id))
    }, DANMAKU_DURATION_MS)

    // 通知 store 已消费, 避免下次 effect 重读同一 payload
    consumePendingDanmaku()

    return () => clearTimeout(timer)
  }, [danmakuTick, pendingDanmaku, consumePendingDanmaku])

  return (
    <div
      className="danmaku-layer"
      aria-live="polite"
      aria-atomic="false"
      // 不挡答题点击, 弹幕纯视觉
      style={{ pointerEvents: 'none' }}
    >
      {items.map(item => {
        const c = categoryColor(item.category)
        return (
          <div
            key={item.id}
            className="danmaku-item"
            style={{
              top: `${12 + item.lane * LANE_HEIGHT}px`,
              '--danmaku-border': c.border,
              '--danmaku-bg': c.bg,
              '--danmaku-text': c.text,
              animationDuration: `${DANMAKU_DURATION_MS - FADE_OUT_AT}ms`,
              animationFillMode: 'forwards',
            } as React.CSSProperties}
          >
            {item.text}
          </div>
        )
      })}
    </div>
  )
}

export default DanmakuLayer
